import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import {
  Box,
  Divider,
  Fab,
  IconButton,
  Slider,
  Tooltip,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import SatelliteAltIcon from "@mui/icons-material/SatelliteAlt";
import MapIcon from "@mui/icons-material/Map";

import LegendBar from "./legendBar";

import styles from "./municipalityMapMenu.module.css";

const MunicipalityMapMenu = ({
  setShow3DMap,
  show3DMap,
  setShow3DBuildings,
  show3DBuildings,
  setActiveRisk,
  activeRisk,
  fireMapMode,
  setFireMapMode,
  selectedEarthquakeLayers,
  setSelectedEarthquakeLayers,
  selectedFloodLayers,
  setSelectedFloodLayers,
  activeMapStyle,
  setActiveMapStyle,
  mapStyle,
  fireHazardColorStops,
  earthquakeHazardColorStops,
  floodHazardColorStops,
  layerOpacities,
  setLayerOpacity,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const isHazardLegendActive =
    activeRisk === "fire" && fireMapMode === "hazard";
  const fireHazardColors = useMemo(
    () => fireHazardColorStops.map(([, color]) => color),
    [fireHazardColorStops],
  );
  const fireHazardLabels = useMemo(() => {
    return [
      t("risksMap.hazardLegend.very_low"),
      t("risksMap.hazardLegend.low"),
      t("risksMap.hazardLegend.medium"),
      t("risksMap.hazardLegend.high"),
      t("risksMap.hazardLegend.very_high"),
    ];
  }, [t]);
  const earthquakeHazardColors = useMemo(
    () => earthquakeHazardColorStops.map(([, color]) => color),
    [earthquakeHazardColorStops],
  );
  const earthquakeHazardLabels = fireHazardLabels;
  const floodHazardColors = useMemo(
    () => floodHazardColorStops.map(([, color]) => color),
    [floodHazardColorStops],
  );
  const floodHazardLevelLabels = useMemo(() => {
    return [
      t("risksMap.hazardLegend.low"),
      t("risksMap.hazardLegend.medium"),
      t("risksMap.hazardLegend.elevated"),
      t("risksMap.hazardLegend.high"),
      t("risksMap.hazardLegend.very_high"),
    ];
  }, [t]);

  const riskConfig = useMemo(
    () => ({
      fire: {
        title: isHazardLegendActive
          ? t("risksMap.hazardLegend.title")
          : t("risksMap.fireLegend.title"),
        colors: isHazardLegendActive
          ? fireHazardColors
          : [
              "#228B22",
              "#804000",
              "#CCF24D",
              "#00FF08",
              "#80FF00",
              "#FFFF80",
              "#FF80FF",
              "#FF0000",
              "#BFBFBF",
              "#0080FF",
              "#00FFFF",
            ],
        labels: isHazardLegendActive
          ? fireHazardLabels
          : [
              t("risksMap.fireLegend.woodyNeedleLeaved"),
              t("risksMap.fireLegend.lowGrowingWoody"),
              t("risksMap.fireLegend.permanentHerbaceous"),
              t("risksMap.fireLegend.woodyBroadleavedEvergreen"),
              t("risksMap.fireLegend.woodyBroadleavedDeciduous"),
              t("risksMap.fireLegend.periodicallyHerbaceous"),
              t("risksMap.fireLegend.lichensAndMosses"),
              t("risksMap.fireLegend.sealed"),
              t("risksMap.fireLegend.nonSparselyVegetated"),
              t("risksMap.fireLegend.water"),
              t("risksMap.fireLegend.snowAndIce"),
            ],
      },
      flood: {
        title: t("risksMap.floodLegend.title"),
        colors: floodHazardColors,
        labels: floodHazardLevelLabels,
      },
      earthquake: {
        title: t("risksMap.earthquakeLegend.title"),
        colors: earthquakeHazardColors,
        labels: earthquakeHazardLabels,
      },
    }),
    [
      t,
      isHazardLegendActive,
      fireHazardColors,
      fireHazardLabels,
      earthquakeHazardColors,
      earthquakeHazardLabels,
      floodHazardColors,
      floodHazardLevelLabels,
    ],
  );

  return (
    <>
      <Fab
        aria-label={t("common.menu")}
        onClick={() => setOpen(!open)}
        className={styles.fab}
      >
        <MenuIcon />
      </Fab>

      {open && (
        <>
          <div className={styles.mapSettings}>
            <IconButton
              aria-label={t("common.hide")}
              onClick={() => setOpen(false)}
              className={styles.closeButton}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <div>
              <Typography sx={{ mb: 1 }} variant="body2">
                {riskConfig[activeRisk].title}
              </Typography>

              {riskConfig[activeRisk].subtitle && (
                <Typography variant="body2">
                  {riskConfig[activeRisk].subtitle}
                </Typography>
              )}

              <LegendBar
                colors={riskConfig[activeRisk].colors}
                labels={riskConfig[activeRisk].labels}
                orientation={
                  (activeRisk === "fire" && fireMapMode === "hazard") ||
                  activeRisk === "earthquake" ||
                  activeRisk === "flood"
                    ? "horizontal"
                    : "vertical"
                }
              />
              {activeRisk === "earthquake" && (
                <Box
                  sx={{
                    mt: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      aria-hidden
                      sx={{
                        width: 14,
                        height: 2,
                        backgroundColor: "#240566",
                        flexShrink: 0,
                      }}
                    />
                    <Typography sx={{ fontSize: 12 }}>
                      {t("risksMap.earthquakeOverlayOptions.tectonicPlates")}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "2px",
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        aria-hidden
                        sx={{
                          width: 5,
                          height: 2,
                          backgroundColor: "hsl(113, 52%, 48%)",
                        }}
                      />
                      <Box
                        aria-hidden
                        sx={{
                          width: 5,
                          height: 2,
                          backgroundColor: "hsl(39, 93%, 47%)",
                        }}
                      />
                      <Box
                        aria-hidden
                        sx={{
                          width: 5,
                          height: 2,
                          backgroundColor: "hsl(0, 89%, 53%)",
                        }}
                      />
                    </Box>
                    <Typography sx={{ fontSize: 12 }}>
                      {t("risksMap.earthquakeOverlayOptions.activeFaults")}
                    </Typography>
                  </Box>
                </Box>
              )}
            </div>

            <Box sx={{ px: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {t("risksMap.layerOpacity")}
              </Typography>
              <Slider
                value={layerOpacities[activeRisk]}
                onChange={(_, value) => setLayerOpacity(activeRisk, value)}
                min={0}
                max={1}
                step={0.05}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => Math.round(v * 100) + "%"}
                size="small"
              />
            </Box>

            <Divider />

            <FormControl component="fieldset" variant="standard">
              <FormGroup
                sx={{
                  ml: "5px",
                  display: "flex",
                  gap: 1,
                  flexDirection: "row",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={show3DMap}
                      onChange={(_e, checked) => setShow3DMap(checked)}
                      name="3D-map"
                      size="small"
                    />
                  }
                  label={t("risksMap.show3d")}
                  componentsProps={{ typography: { variant: "body2" } }}
                />
                <Tooltip
                  title={
                    activeMapStyle === mapStyle.satellite
                      ? t("risksMap.show3dBuildingsSatelliteDisabled")
                      : ""
                  }
                >
                  <span>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={
                            activeMapStyle !== mapStyle.satellite &&
                            show3DBuildings
                          }
                          onChange={(_e, checked) =>
                            setShow3DBuildings(checked)
                          }
                          name="3D-buildings"
                          size="small"
                          disabled={activeMapStyle === mapStyle.satellite}
                        />
                      }
                      label={t("risksMap.show3dBuildings")}
                      componentsProps={{ typography: { variant: "body2" } }}
                    />
                  </span>
                </Tooltip>
              </FormGroup>
            </FormControl>

            <ToggleButtonGroup
              value={activeMapStyle}
              exclusive
              onChange={(e, value) => {
                if (value) {
                  setActiveMapStyle(value);
                }
              }}
              size="small"
              color="primary"
              sx={{ "& .MuiToggleButton-root": { fontSize: 14 } }}
            >
              <ToggleButton value={mapStyle.street}>
                <MapIcon sx={{ mr: 1 }} fontSize="small" />
                {t("common.street")}
              </ToggleButton>
              <ToggleButton value={mapStyle.satellite}>
                <SatelliteAltIcon sx={{ mr: 1 }} fontSize="small" />
                {t("common.satellite")}
              </ToggleButton>
            </ToggleButtonGroup>

            {activeRisk === "fire" && (
              <FormControl component="fieldset" variant="standard">
                <FormLabel component="legend" sx={{ mb: "5px", fontSize: 14 }}>
                  {t("risksMap.selectFireMapVariant")}
                </FormLabel>
                <ToggleButtonGroup
                  value={fireMapMode}
                  exclusive
                  onChange={(_e, value) => {
                    if (value) {
                      setFireMapMode(value);
                    }
                  }}
                  size="small"
                  color="primary"
                >
                  <ToggleButton value="hazard">
                    {t("risksMap.fireMapVariants.hazard")}
                  </ToggleButton>
                  <ToggleButton value="terrain">
                    {t("risksMap.fireMapVariants.terrain")}
                  </ToggleButton>
                </ToggleButtonGroup>
              </FormControl>
            )}

            {activeRisk === "earthquake" && (
              <FormControl component="fieldset" variant="standard">
                <FormLabel component="legend" sx={{ mb: "5px", fontSize: 14 }}>
                  {t("risks.earthquake")}
                </FormLabel>
                <ToggleButtonGroup
                  value={selectedEarthquakeLayers}
                  onChange={(_e, value) => {
                    setSelectedEarthquakeLayers(value);
                  }}
                  size="small"
                  color="primary"
                >
                  <ToggleButton value="hazard">
                    {t("risksMap.earthquakeOverlayOptions.hazard")}
                  </ToggleButton>
                  <ToggleButton value="tectonicPlates">
                    {t("risksMap.earthquakeOverlayOptions.tectonicPlates")}
                  </ToggleButton>
                  <ToggleButton value="activeFaults">
                    {t("risksMap.earthquakeOverlayOptions.activeFaults")}
                  </ToggleButton>
                </ToggleButtonGroup>
              </FormControl>
            )}

            {activeRisk === "flood" && (
              <FormControl component="fieldset" variant="standard">
                <FormLabel component="legend" sx={{ mb: "5px", fontSize: 14 }}>
                  {t("risks.flood")}
                </FormLabel>
                <ToggleButtonGroup
                  value={selectedFloodLayers}
                  onChange={(_e, value) => {
                    setSelectedFloodLayers(value);
                  }}
                  size="small"
                  color="primary"
                >
                  <ToggleButton value="hazard">
                    {t("risksMap.floodOverlayOptions.hazard")}
                  </ToggleButton>
                  <ToggleButton value="rivers">
                    {t("risksMap.floodOverlayOptions.rivers")}
                  </ToggleButton>
                  <ToggleButton value="lakes">
                    {t("risksMap.floodOverlayOptions.lakes")}
                  </ToggleButton>
                </ToggleButtonGroup>
              </FormControl>
            )}

            <FormControl component="fieldset" variant="standard">
              <FormLabel component="legend" sx={{ mb: "5px", fontSize: 14 }}>
                {t("risksMap.selectRiskMap")}
              </FormLabel>
              <ToggleButtonGroup
                value={activeRisk}
                exclusive
                onChange={(e, value) => {
                  if (value) {
                    setActiveRisk(value);
                  }
                }}
                size="small"
                color="primary"
                sx={{ "& .MuiToggleButton-root": { fontSize: 14 } }}
              >
                <ToggleButton value="fire">{t("risks.fire")}</ToggleButton>
                <ToggleButton value="flood">{t("risks.flood")}</ToggleButton>
                <ToggleButton value="earthquake">
                  {t("risks.earthquake")}
                </ToggleButton>
              </ToggleButtonGroup>
            </FormControl>
          </div>
        </>
      )}
    </>
  );
};

export default MunicipalityMapMenu;
